import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  MdAttachFile, MdUpload, MdDelete, MdDownload, MdPictureAsPdf,
  MdImage, MdDescription, MdTableChart, MdInsertDriveFile
} from 'react-icons/md';
import { adjuntosAPI } from '../services/api';
import { useModal } from '../context/ModalContext';

const Section = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  margin-top: ${p => p.theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const Title = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg};
  color: ${p => p.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin: 0;
  svg { color: ${p => p.theme.colors.primary}; }
`;

const UploadBtn = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px ${p => p.theme.colors.primary}40; }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const DropZone = styled.div`
  border: 2px dashed ${p => p.dragging ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  text-align: center;
  background: ${p => p.dragging ? p.theme.colors.primary + '08' : p.theme.colors.background};
  transition: all 0.2s;
  cursor: pointer;
  margin-bottom: ${p => p.theme.spacing.lg};

  svg { font-size: 2.5rem; color: ${p => p.theme.colors.textLight}; margin-bottom: ${p => p.theme.spacing.sm}; }
  p { color: ${p => p.theme.colors.textSecondary}; font-size: ${p => p.theme.fontSizes.sm}; margin: 0; }
  small { color: ${p => p.theme.colors.textLight}; font-size: ${p => p.theme.fontSizes.xs}; }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.borderLight};
  transition: ${p => p.theme.transitions.fast};

  &:hover { border-color: ${p => p.theme.colors.border}; }
`;

const FileIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${p => p.bg || p.theme.colors.primary + '15'};
  svg { font-size: 1.4rem; color: ${p => p.color || p.theme.colors.primary}; }
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.a`
  display: block;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover { color: ${p => p.theme.colors.primary}; }
`;

const FileMeta = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
`;

const ActionBtn = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  background: ${p => p.danger ? p.theme.colors.danger + '15' : p.theme.colors.backgroundLight};
  color: ${p => p.danger ? p.theme.colors.danger : p.theme.colors.textSecondary};
  svg { font-size: 1rem; }
  &:hover { background: ${p => p.danger ? p.theme.colors.danger + '25' : p.theme.colors.border}; }
`;

const EmptyMsg = styled.p`
  color: ${p => p.theme.colors.textLight};
  font-size: ${p => p.theme.fontSizes.sm};
  text-align: center;
  padding: ${p => p.theme.spacing.lg};
  margin: 0;
`;

const getFileIcon = (mime) => {
  if (!mime) return { Icon: MdInsertDriveFile, color: '#718096', bg: '#71809615' };
  if (mime.startsWith('image/')) return { Icon: MdImage, color: '#48BB78', bg: '#48BB7815' };
  if (mime === 'application/pdf') return { Icon: MdPictureAsPdf, color: '#F56565', bg: '#F5656515' };
  if (mime.includes('word')) return { Icon: MdDescription, color: '#4299E1', bg: '#4299E115' };
  if (mime.includes('excel') || mime.includes('spreadsheet') || mime.includes('csv'))
    return { Icon: MdTableChart, color: '#48BB78', bg: '#48BB7815' };
  return { Icon: MdInsertDriveFile, color: '#718096', bg: '#71809615' };
};

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const Adjuntos = ({ entidadTipo, entidadId }) => {
  const { confirm, toast } = useModal();
  const [adjuntos, setAdjuntos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    load();
  }, [entidadTipo, entidadId]); // eslint-disable-line

  const load = async () => {
    try {
      setLoading(true);
      const res = await adjuntosAPI.getByEntidad(entidadTipo, entidadId);
      setAdjuntos(res.data.data || []);
    } catch {
      setAdjuntos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('archivo', file);
        const res = await adjuntosAPI.upload(entidadTipo, entidadId, fd);
        setAdjuntos(prev => [res.data.data, ...prev]);
        uploaded++;
      } catch (err) {
        toast(err.message || `Error al subir ${file.name}`, 'error');
      }
    }
    if (uploaded > 0) toast(`${uploaded} archivo(s) subido(s)`, 'success');
    setUploading(false);
  };

  const handleDelete = async (adj) => {
    const ok = await confirm(`¿Eliminar "${adj.nombre_original}"?`, {
      title: 'Eliminar archivo',
      confirmText: 'Eliminar',
      danger: true
    });
    if (!ok) return;
    try {
      await adjuntosAPI.delete(adj.id);
      setAdjuntos(prev => prev.filter(a => a.id !== adj.id));
      toast('Archivo eliminado', 'success');
    } catch (err) {
      toast(err.message || 'Error al eliminar', 'error');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <Section>
      <Header>
        <Title><MdAttachFile /> Documentos Adjuntos</Title>
        <UploadBtn onClick={() => inputRef.current?.click()} disabled={uploading}>
          <MdUpload />
          {uploading ? 'Subiendo...' : 'Subir archivo'}
        </UploadBtn>
      </Header>

      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        onChange={e => handleFiles(e.target.files)}
      />

      <DropZone
        dragging={dragging}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <MdUpload />
        <p>Arrastra archivos aqui o haz clic para seleccionar</p>
        <small>PDF, Word, Excel, imagenes — max. 20 MB por archivo</small>
      </DropZone>

      {loading ? (
        <EmptyMsg>Cargando...</EmptyMsg>
      ) : adjuntos.length === 0 ? (
        <EmptyMsg>Sin archivos adjuntos aun</EmptyMsg>
      ) : (
        <FileList>
          {adjuntos.map(adj => {
            const { Icon, color, bg } = getFileIcon(adj.tipo_mime);
            const url = adjuntosAPI.getUrl(adj.nombre_archivo);
            return (
              <FileItem key={adj.id}>
                <FileIcon bg={bg} color={color}><Icon /></FileIcon>
                <FileInfo>
                  <FileName href={url} target="_blank" rel="noreferrer">
                    {adj.nombre_original}
                  </FileName>
                  <FileMeta>
                    {formatBytes(adj.tamanio)}
                    {adj.created_at && ` · ${new Date(adj.created_at).toLocaleDateString('es-MX')}`}
                  </FileMeta>
                </FileInfo>
                <ActionBtn as="a" href={url} download={adj.nombre_original} title="Descargar">
                  <MdDownload />
                </ActionBtn>
                <ActionBtn danger onClick={() => handleDelete(adj)} title="Eliminar">
                  <MdDelete />
                </ActionBtn>
              </FileItem>
            );
          })}
        </FileList>
      )}
    </Section>
  );
};

export default Adjuntos;
